import React from 'react';

export interface ColunaKanban {
  chave: string;
  titulo: string;
}

interface QuadroKanbanProps<T> {
  colunas: ColunaKanban[];
  itens: T[];
  /** Mapeia um item pra chave da coluna onde ele entra; `null` = não entra em nenhuma. */
  colunaDoItem: (item: T) => string | null;
  renderCard: (item: T) => React.ReactNode;
  keyDoItem: (item: T) => React.Key;
  /** Palavra do vazio de coluna, ex.: `vazioLabel="pendência"` → "Sem pendência". */
  vazioLabel?: string;
}

/**
 * Board Kanban genérico (read-only, sem arrastar): colunas fixas, itens distribuídos por
 * `colunaDoItem`, cada coluna com título + contagem. Tokens Íris (≠ o KanbanBoard legado dos
 * Pedidos, acoplado + zinc). Genérico de propósito, mas hoje só a tela de Pendências consome —
 * `colunaDoItem`/`renderCard` são a cola específica do domínio, o board só agrupa e desenha.
 */
export function QuadroKanban<T>({
  colunas,
  itens,
  colunaDoItem,
  renderCard,
  keyDoItem,
  vazioLabel = 'itens',
}: QuadroKanbanProps<T>) {
  const porColuna = new Map<string, T[]>();
  for (const col of colunas) porColuna.set(col.chave, []);
  for (const item of itens) {
    const chave = colunaDoItem(item);
    if (chave && porColuna.has(chave)) porColuna.get(chave)!.push(item);
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {colunas.map((col) => {
        const cards = porColuna.get(col.chave) ?? [];
        return (
          <section
            key={col.chave}
            className="flex w-72 flex-shrink-0 flex-col rounded-iris-lg border border-line bg-surface-2"
          >
            <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
              <span className="truncate text-sm font-semibold text-tx">{col.titulo}</span>
              <span className="flex-shrink-0 rounded-full bg-surface px-2 py-0.5 text-xs font-medium tabular-nums text-tx-2">
                {cards.length}
              </span>
            </header>
            <div className="flex flex-col gap-2 p-2">
              {cards.length === 0 ? (
                <p className="px-1 py-6 text-center text-xs text-tx-3">Sem {vazioLabel}</p>
              ) : (
                cards.map((item) => (
                  <React.Fragment key={keyDoItem(item)}>{renderCard(item)}</React.Fragment>
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default QuadroKanban;
